import { api_configuration } from "../../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import { client_state } from "../../state/ClientState";
import * as createMutation from "../../state 2 interface/createMutation";
import messages = require("../../front/messages");
import { uploadFilesInQueue } from "./fileUpload";
import { InstructionError } from "./InstructionError";
import { DatastoreProxy } from "../../interface/DatastoreProxy";
import * as reQuery from "../../state 2 interface/reQuery";
import * as Entry from "../../state/Entry";
import clientWorkIsDone from "../../state/tasks";
import { iCollection } from "../../state/Collection";
import resetModifiedState from "../../state/resetModifiedState";

async function uploadFilesAndVerifyValidAgain(state: api_cs.Cnode): Promise<void> {
	await uploadFilesInQueue(state);
	assertIsValid(state);
}

const save_handlers: ((node: api_cs.Cnode) => void)[] = [];
export function subscribeOnSave(callback: (node: api_cs.Cnode) => void): (node: api_cs.Cnode) => void {
	save_handlers.push(callback);
	return callback;
}

export function unSubscribeOnSave(callback: (node: api_cs.Cnode) => void): void {
	const index = save_handlers.indexOf(callback);
	save_handlers.splice(index, 1);
}

export async function saveAll(node: api_cs.Cnode) {
	return entity(node, true);
}

export async function entity(state: api_cs.Cnode, save_all = false): Promise<void> {
	if (state.destroyed) return;
	messages.removeAllMessages();
	await clientWorkIsDone();
	if (state.destroyed) throw new InstructionError("Node has been destroyed");
	try {
		state.properties.is_busy_saving.ensure("yes", {});
		const location = state.input.position();
		switch (location.variant.name) {
			case "entity": {
				const entity = location.variant.definition;
				switch (entity.variant.name) {
					case "root":
						await saveNodeToBackend(state, save_all);
						break;
					case "entry": {
						const entry = entity.variant.definition;
						const parent_node = entry.parent.parent.parent.parent;
						switch (parent_node.properties.location.state.name) {
							case "remote": {
								switch (entry.properties.location.state.name) {
									case "remote": {
										const remote_entry = entry.properties.location.state.node;
										switch (remote_entry.properties.marked_for_deletion.state.name) {
											case "yes":
												break;
											case "no":
												await assertEntryValid(entry);
										}
										break;
									}
									case "local":
										await assertEntryValid(entry);
								}
								if (entry.destroyed === true) return;
								if (iCollection(entry.parent).graphNodeIsModified(entry)) {
									const collection = entry.parent;
									assertCollectionIsValid(collection);
									const mutation = createMutation.forCollection(collection, save_all);
									await sendMutation(entry.component_root, mutation);
								} else {
									const mutation = createMutation.forEntry(entry, save_all);
									await sendMutation(entry.component_root, mutation);
								}
								if (!entry.destroyed) {
									requerySubCollections(entry.properties.node);
									reQuery.queriesForCollection(entry);
								}
								break;
							}
							case "local":
							// Do nothing
						}
						break;
					}
				}
				break;
			}
			case "group":
				await saveNodeToBackend(state, save_all);
				break;
			case "root": {
				const root = location.variant.definition;
				switch (root.variant.name) {
					case "operation":
						break;
					case "root":
						await saveNodeToBackend(state, save_all);
				}
				break;
			}
			case "state":
				await saveNodeToBackend(state, save_all);
		}
		if (!state.destroyed) {
			resetModifiedState(state, true);
			save_handlers.forEach((handler) => handler(state));
		}
	} finally {
		if (!state.destroyed) {
			state.properties.is_busy_saving.ensure("no", {});
		}
	}
}

function assertIsValid(node: api_cs.Cnode) {
	switch (node.properties.is_valid.state.name) {
		case "no":
			messages.addMessage("warning", "Changes not saved", {
				body: "There are invalid fields remaining. Check all fields and try again.",
			});
			throw new InstructionError("State is not valid");
		case "yes":
	}
}

function assertCollectionIsValid(collection: api_cs.Ccollection) {
	switch (collection.properties.is_valid.state.name) {
		case "no":
			messages.addMessage("warning", "Changes not saved", {
				body: "There are invalid fields remaining. Check all fields and try again.",
			});
			throw new InstructionError("State is not valid");
		case "yes":
	}
}

async function assertEntryValid(entry: api_cs.Centries__collection) {
	await Entry.keyInitialized(entry);
	const key = entry.properties.key.inferences.key();
	switch (key.properties.key_unique.state.name) {
		case "no":
			messages.addMessage("warning", "Changes not saved", {
				body: "Name is not valid or already in use.",
			});
			throw new InstructionError("Name is not valid or already in use.");
		case "yes":
	}
	assertIsValid(entry.properties.node);
	await uploadFilesAndVerifyValidAgain(entry.properties.node);
}

async function saveNodeToBackend(state: api_cs.Cnode, save_all: boolean) {
	assertIsValid(state);
	await uploadFilesAndVerifyValidAgain(state);
	if (state.destroyed === true) return;
	await sendMutation(state, createMutation.forNode(state, save_all));
}

export async function sendMutation(node: api_cs.Cnode, mutation_object: createMutation.MutationObject): Promise<void> {
	const mutation_reply = await DatastoreProxy.instance.update(mutation_object.mutation);
	if (node.destroyed) {
		throw new InstructionError("Node removed");
	}
	mutation_reply.properties.context.switch({
		"does not exist": function () {
			messages.addMessage("warning", "Item not saved", { body: "Parent item removed." });
			mutation_object.onFailed(false);
			throw new InstructionError();
		},
		"permission denied": () => {
			messages.addMessage("warning", "Item not saved", {
				body: "Permission denied. Permissions may have changed while you were editing. Try saving again.",
			});
			mutation_object.onFailed(true);
			throw new InstructionError();
		},
		exists: function (yes__context_exists) {
			yes__context_exists.properties.result.switch({
				success: () => {
					mutation_object.onSucces();
				},
				failure: () => {
					messages.addMessage("error", "Item not saved", {
						body: "Item cannot be saved, the datastore did not accept your changes.",
					});
					mutation_object.onFailed(false);
					throw new InstructionError();
				},
			});
		},
	});
}

function requerySubCollections(node: api_cs.Cnode) {
	node.properties.queries.forEach((query) => {
		query.properties.query.properties.delay_query.switch({
			no: () => {
				reQuery.query(query.properties.query);
			},
			yes: undefined,
		});
	});
	node.properties.attributes.forEach(function ($) {
		$.properties.type.switch({
			operation: undefined,
			property: function ($) {
				$.properties.type.switch({
					collection: undefined,
					file: undefined,
					group: function ($) {
						requerySubCollections($.properties.node);
					},
					number: undefined,
					"state group": function ($) {
						$.properties.states.forEach(function ($) {
							requerySubCollections($.properties.node);
						});
					},
					text: undefined,
				});
			},
		});
	});
}
